import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import appConfig from 'config'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import type { AppStore } from 'state/store'
import type logoutUserType from 'state/user/actions/logoutUser'

let _store: AppStore | undefined
let _logoutUser: typeof logoutUserType | undefined

export function injectDependencies(
    store: AppStore,
    logoutUser: typeof logoutUserType,
) {
    _store = store
    _logoutUser = logoutUser
}

/**
 * Global state to track 401 errors and block subsequent requests
 */
let isUnauthorized = false

let pendingRequests: Array<{
    config: InternalAxiosRequestConfig
    resolve: (
        value:
            | InternalAxiosRequestConfig
            | PromiseLike<InternalAxiosRequestConfig>,
    ) => void
    reject: (reason?: unknown) => void
}> = []

/**
 * Returns true if a 401 from our API has been detected and session expiry is in progress
 */
export function isUnauthorizedState(): boolean {
    return isUnauthorized
}

/**
 * Reset the unauthorized state (for testing or after re-authentication)
 */
export function resetUnauthorizedState() {
    isUnauthorized = false
    pendingRequests = []
}

/**
 * Clear all pending requests with a 401 error
 */
function clearPendingRequests() {
    const queued = pendingRequests

    pendingRequests = []

    queued.forEach((request) => {
        request.reject(
            Object.assign(
                new Error('Session expired - subsequent requests blocked'),
                {
                    isAxiosError: true,
                    response: {
                        status: 401,
                        statusText: 'Unauthorized',
                        data: { detail: 'Session expired' },
                    },
                },
            ),
        )
    })
}

/**
 * Handle session expiry on 401. Clears pending requests and dispatches logout.
 * LoginManager renders <LoginPage /> once isLoggedIn=false, no hash redirect needed.
 */
function handleSessionExpiry() {
    try {
        if (!_store || !_logoutUser) {
            throw new Error('unauthorizedInterceptor dependencies not injected')
        }

        _store.dispatch(
            _logoutUser({ skipRedirect: true, skipLogoutLegacyApp: true }),
        )
    } catch (error) {
        OctaneLogger.error(
            '[401] Failed to dispatch logout',
            undefined,
            error as Error,
        )
    }
}

/** Block stale requests locally without starting another logout request. */
export function markSessionEnded() {
    isUnauthorized = true
    clearPendingRequests()
}

/**
 * Mark the session as expired and trigger the graceful logout flow.
 * No-op if already in the unauthorized state, so concurrent 401s
 * (e.g. from the two independent ping call sites) only log out once.
 */
export function triggerSessionExpiry() {
    if (isUnauthorized) return

    markSessionEnded()
    handleSessionExpiry()
}

/**
 * Check if a request should be exempt from 401 blocking
 */
function isExemptFromBlocking(url?: string): boolean {
    if (!url) return false

    const exemptPatterns = [
        '/auth/',
        '/invitation/',
        '/password-reset/',
        '/users/',
        '/waffle-status',
        'zipcodesByState.json',
    ]

    return exemptPatterns.some((pattern) => url.includes(pattern))
}

/**
 * Request interceptor: Block requests if we're in unauthorized state
 */
export function unauthorizedRequestInterceptor(
    config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
    if (isExemptFromBlocking(config.url)) {
        return Promise.resolve(config)
    }

    if (isUnauthorized) {
        const p = new Promise<InternalAxiosRequestConfig>((resolve, reject) => {
            pendingRequests.push({ config, resolve, reject })
        })

        p.catch(() => {}) // prevent unhandled rejection in Node 18

        return p
    }

    return Promise.resolve(config)
}

/**
 * Response interceptor: Detect 401s and block subsequent requests
 */
export function unauthorizedResponseInterceptor(
    error: unknown,
): Promise<never> {
    if (!axios.isAxiosError(error)) {
        return Promise.reject(error)
    }

    const axiosError = error as AxiosError
    const url = axiosError.config?.url || ''
    const baseURL = axiosError.config?.baseURL || ''

    // Ignore 401s from third-party requests (url is a full URL for global axios,
    // baseURL is set for api instance requests)
    const isOurApiRequest =
        url.startsWith(appConfig.API_BASE_URL) ||
        baseURL.startsWith(appConfig.API_BASE_URL)

    if (!isOurApiRequest) {
        return Promise.reject(error)
    }

    if (axiosError.response?.status === 401) {
        if (url.includes('api/v3/auth/ping')) {
            return Promise.reject(error)
        }

        if (isExemptFromBlocking(url)) {
            return Promise.reject(error)
        }

        triggerSessionExpiry()
    }

    return Promise.reject(error)
}

/**
 * Register the unauthorized interceptors
 */
export function registerUnauthorizedInterceptors() {
    axios.interceptors.request.use(unauthorizedRequestInterceptor, (error) =>
        Promise.reject(error),
    )

    axios.interceptors.response.use(
        (response) => response,
        unauthorizedResponseInterceptor,
    )
}
